'use client'

import { memo, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { useDebounce } from 'use-debounce'
import { useRecoilState } from 'recoil'
import PublicBookmarkListContainer from './PublicBookmarkListContainer'
import PublicSearchHeader from './PublicSearchHeader'
import useRect from '../../utils/hooks/useRect'
import { publicBookmarksSearchMode, publicBookmarksSortMode, publicBookmarksViewMode } from '../../store/public'
import MaxWidth from '../MaxWidth'
import FolderUsers from './FolderUsers'

const styles = {
  listWrapper: (padding, topHeaderHeight) => css`
    padding: ${padding} !important;
    width: 100%;
    height: calc(100% - ${topHeaderHeight}px);
    overflow-y: auto;
    overflow-x: hidden;
  `,
}

const menuSearchOptions = [
  { label: 'Title', value: 'title' },
  { label: 'Description', value: 'description' },
  { label: 'URL', value: 'url' },
  { label: 'Full-body text', value: 'text' },
]

const sortModeOptions = [
  { label: 'Creation date Asc ↑', value: 'createdAt' },
  { label: 'Creation date Des ↓', value: '-createdAt', default: true },
  { label: 'Bookmark title Asc (A-Z)', value: 'title' },
  { label: 'Bookmark title Des (Z-A)', value: '-title' },
]

const viewModeOptions = [
  { label: 'Type card', value: 'viewLikeCard' },
  { label: 'Show URL', value: 'showUrl' },
  { label: 'Show description', value: 'showDescription' },
]

function PublicListSearchContent({ folderId, padding, userId, bookmarks, isErrorBookmarks, folderAndCount }) {
  const [inputValue, setInputValue] = useState('')
  const [scrollPassLimit, setScrollPassLimit] = useState(false)
  const [largeDebounceTime, setLargeDebounceTime] = useState(false)
  const debounceTime = largeDebounceTime ? 350 : 0
  const [value] = useDebounce(inputValue, debounceTime)
  const [bookmarksViewMode, setBookmarksViewMode] = useRecoilState(publicBookmarksViewMode)
  const [bookmarksSortMode, setBookmarksSortMode] = useRecoilState(publicBookmarksSortMode)
  const [bookmarksSearchMode, setBookmarksSearchMode] = useRecoilState(publicBookmarksSearchMode)

  const headerRef = useRef()
  const { height: topHeaderHeight } = useRect(headerRef)

  // Find active folder name
  const activeFolderName = useMemo(() => {
    if (folderId === folderAndCount.folder._id) {
      return folderAndCount.folder.name
    }

    const activeFolder = folderAndCount.childFolders.find(folder => folder._id.toString() === folderId.toString())

    return activeFolder ? activeFolder.name : ''
  }, [folderId, folderAndCount])

  const or = useMemo(() => {
    if (!value) return
    return {
      $or: menuSearchOptions
        .filter(option => bookmarksSearchMode[option.value])
        .map(option => ({
          [option.value]: { $regex: value, $options: 'i' },
        })),
    }
  }, [bookmarksSearchMode, value, menuSearchOptions])

  const options = useMemo(() => {
    const orOpt = or ?? {}
    return {
      params: {
        options: {
          folder: folderId,
          ...orOpt,
        },
        sort: bookmarksSortMode,
      },
    }
  }, [or, bookmarksSortMode, folderId])

  return (
    <>
      <PublicSearchHeader
        ref={headerRef}
        padding={padding}
        setInputValue={setInputValue}
        inputValue={inputValue}
        menuSearchOptions={menuSearchOptions}
        viewMode={bookmarksViewMode}
        setViewMode={setBookmarksViewMode}
        sortMode={bookmarksSortMode}
        setSortMode={setBookmarksSortMode}
        searchMode={bookmarksSearchMode}
        setSearchMode={setBookmarksSearchMode}
        sortModeOptions={sortModeOptions}
        scrollPassLimit={scrollPassLimit}
        viewModeOptions={viewModeOptions}
        setLargeDebounceTime={setLargeDebounceTime}
        folderId={folderId}
        userId={userId}
        folderAndCount={folderAndCount}
      />
      <MaxWidth
        css={styles.listWrapper(padding, topHeaderHeight)}
        className="custom-scrollbar-guardo"
        onScroll={e => {
          const { scrollTop } = e.target
          if (scrollTop > 10) {
            if (!scrollPassLimit) return setScrollPassLimit(true)
            return
          }
          if (scrollPassLimit) setScrollPassLimit(false)
        }}
      >
        {bookmarks?.length ? (
          <PublicBookmarkListContainer
            options={options}
            padding={padding}
            isSearching={!!inputValue}
            bookmarks={bookmarks}
            userId={userId}
            folderId={folderId}
            bookmarksViewMode={bookmarksViewMode}
            isErrorBookmarks={isErrorBookmarks}
            activeFolderName={activeFolderName}
          />
        ):''}

        <FolderUsers folder={folderAndCount.folder} />
      </MaxWidth>
    </>
  )
}

export default memo(PublicListSearchContent)
